/* eslint-disable @typescript-eslint/no-explicit-any */
import GenericToaster from "components/Toaster";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import moment from "moment";
import apiService from "services/apiService";
// Import your validation utility (adjust the path if needed)
import { TrailingOrLeadingWhitespaceValidation } from "util/validation";
// The "Asset Type" dropdown (device kinds requested) is fed by the Device Type
// admin config, rendered dynamically instead of the old hardcoded list.
import { useInventoryOptions } from "./useInventoryConfig";

const PRIORITY_OPTIONS = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];

// Note: badge colors mirror the listing table (InventoryRequestList) so the
// view detail stays visually consistent with the rest of the module.
const PRIORITY_COLOR: Record<string, string> = {
  LOW: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  MEDIUM: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
  HIGH: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  CRITICAL: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

const STATUS_COLOR: Record<string, string> = {
  PENDING: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
  APPROVED: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  REJECTED: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

interface AssetRequestPayload {
  assetType: string;
  priority: string;
  reason: string;
  preferredBrand?: string;
  preferredModel?: string;
  adminComment?: string
}

// Full request record used only to render the read-only view detail.
export interface AssetRequestViewData {
  userName?: string;
  assetType?: string;
  priority?: string;
  reason?: string;
  preferredBrand?: string | null;
  preferredModel?: string | null;
  status?: string;
  adminComment?: string | null;
  approvedBy?: string | null;
  approvedAt?: number | null;
  createdAt?: number | null;
}

interface InventoryRequestFormProps {
  onSuccess?: (toastData: { message: string; severity: "info" | "error" }) => void;
  onClose?: () => void;
  requestId?: number;        // present → edit mode
  initialData?: Partial<AssetRequestPayload>;
  viewMode?: boolean;
  viewData?: AssetRequestViewData;   // full record shown in read-only view mode
}

// Small badge used in the read-only detail (matches the listing table style).
const Badge = ({ text, colorClass }: { text: string; colorClass: string }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${colorClass}`}>
    {text}
  </span>
);

// Label + value pair used across the read-only detail grid.
const DetailField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {label}
    </span>
    <div className="text-sm text-gray-900 dark:text-white break-words">{children}</div>
  </div>
);

// Format epoch (ms) → readable date-time; returns "-" for missing values.
const formatEpoch = (epoch?: number | null): string =>
  epoch ? moment(epoch).format("DD-MM-YYYY, hh:mm A") : "-";

const InventoryRequestForm = ({ onSuccess, onClose, requestId, initialData, viewMode = false, viewData }: InventoryRequestFormProps) => {
  const isEditMode = !!requestId;
  // Device Type options from Admin Config feed the "Asset Type" dropdown.
  const { deviceTypeOptions } = useInventoryOptions();
  const methods = useForm<AssetRequestPayload>({
    defaultValues: {
      assetType: initialData?.assetType ?? "",
      priority: initialData?.priority ?? "MEDIUM",
      reason: initialData?.reason ?? "",
      preferredBrand: initialData?.preferredBrand ?? "",
      preferredModel: initialData?.preferredModel ?? "",
    },
  });

  React.useEffect(() => {
    if (initialData) {
      methods.reset({
        assetType: initialData.assetType ?? "",
        priority: initialData.priority ?? "MEDIUM",
        reason: initialData.reason ?? "",
        preferredBrand: initialData.preferredBrand ?? "",
        preferredModel: initialData.preferredModel ?? "",
      });
    }
  }, [initialData, methods]);

  const [toastData, setToastData] = useState<{
    message: string;
    severity: "info" | "error";
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = (data: AssetRequestPayload) => {
    // Strip empty optional fields
    const payload: Partial<AssetRequestPayload> = {
      assetType: data.assetType,
      priority: data.priority,
      reason: data.reason,
    };
    if (data.preferredBrand?.trim()) payload.preferredBrand = data.preferredBrand.trim();
    if (data.preferredModel?.trim()) payload.preferredModel = data.preferredModel.trim();

    setSubmitting(true);
    const apiCall = isEditMode
      ? apiService.put(`/api/assetRequests/my/${requestId}`, payload)
      : apiService.post("/api/assetRequests", payload);
    apiCall
      .then(() => {
        const successToast = {
          message: isEditMode
            ? "Request updated successfully!"
            : "Asset request submitted successfully!",
          severity: "info" as const,
        };

        methods.reset();

        /**
         * NOTE: the success toast has to be rendered by whoever stays mounted.
         * This form lives inside a modal, so setting its own toast state and
         * then closing unmounted the toaster in the same tick and nothing was
         * ever shown. When a parent supplies onSuccess it owns the toast (it
         * outlives the modal), and only then is it safe to auto-close here.
         * Without onSuccess the inline toaster is still the only one available,
         * so the modal stays open to display it — the previous behaviour.
         */
        if (onSuccess) {
          onSuccess(successToast);
          onClose?.();
        } else {
          setToastData(successToast);
        }
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || err?.response?.data || "Failed to submit asset request.";
        setToastData({ message: msg, severity: "error" });
      })
      .finally(() => setSubmitting(false));
  };

  const {
    register,
    formState: { errors },
    watch,
  } = methods;
  const selectedAssetType = watch("assetType");
  const selectedPriority = watch("priority");

  // Read-only detail view — renders the full request record (falls back to
  // initialData for the basic fields when viewData isn't provided).
  if (viewMode) {
    const d: AssetRequestViewData = viewData ?? (initialData as AssetRequestViewData) ?? {};
    return (
      <div className="px-3 pt-0 pb-2 flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {d.userName != null && d.userName !== "" && (
            <DetailField label="User Name">{d.userName}</DetailField>
          )}
          {d.status && (
            <DetailField label="Status">
              <Badge text={d.status} colorClass={STATUS_COLOR[d.status] ?? ""} />
            </DetailField>
          )}
          <DetailField label="Asset Type">{d.assetType || "-"}</DetailField>
          <DetailField label="Priority">
            {d.priority ? (
              <Badge text={d.priority} colorClass={PRIORITY_COLOR[d.priority] ?? ""} />
            ) : (
              "-"
            )}
          </DetailField>
          {d.preferredBrand && (
            <DetailField label="Preferred Brand">{d.preferredBrand}</DetailField>
          )}
          {d.preferredModel && (
            <DetailField label="Preferred Model">{d.preferredModel}</DetailField>
          )}
          {d.createdAt != null && (
            <DetailField label="Requested On">{formatEpoch(d.createdAt)}</DetailField>
          )}
          {d.approvedBy && <DetailField label="Actioned By">{d.approvedBy}</DetailField>}
          {d.approvedAt != null && (
            <DetailField label="Actioned On">{formatEpoch(d.approvedAt)}</DetailField>
          )}
        </div>

        {/* Reason — full width, scrollable so very long text stays contained */}
        <DetailField label="Reason">
          <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-3 py-2 whitespace-pre-wrap break-words">
            {d.reason || "-"}
          </div>
        </DetailField>

        {/* Admin comment — only shown when the request has been actioned */}
        {d.adminComment && (
          <DetailField label="Admin Comment">
            <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-3 py-2 whitespace-pre-wrap break-words">
              {d.adminComment}
            </div>
          </DetailField>
        )}

        {/* Footer */}
        <div className="mt-1 flex justify-end px-2 gap-2">
          <button
            type="button"
            onClick={onClose}
            className="ms-3 text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="px-3 pt-0 pb-2 flex flex-col gap-4"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Asset Type */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Asset Type <span className="text-red-500">*</span>
              </label>
              {viewMode ? (
                <div className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm px-3 py-2 text-gray-900 dark:text-white">
                  {selectedAssetType || "-"}
                </div>
              ) : (
                <select
                  {...register("assetType", { required: "Asset type is required" })}
                  className={`w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm px-3 py-2 focus:ring-primary focus:border-primary ${selectedAssetType ? "text-gray-900 dark:text-white" : "text-gray-400 dark:text-gray-400"
                    }`}
                >
                  <option value="" disabled hidden>
                    Asset Type
                  </option>
                  {deviceTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value} className="text-gray-900 dark:text-white">
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
              {errors.assetType && (
                <span className="text-red-500 text-xs">{errors.assetType.message}</span>
              )}
            </div>

            {/* Priority */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Priority <span className="text-red-500">*</span>
              </label>
              {viewMode ? (
                <div className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2">
                  {selectedPriority || "-"}
                </div>
              ) : (
                <select
                  {...register("priority", { required: "Priority is required" })}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:ring-primary focus:border-primary"
                >
                  {PRIORITY_OPTIONS.map((p) => (
                    <option key={p} value={p} className="text-gray-900 dark:text-white">
                      {p}
                    </option>
                  ))}
                </select>
              )}
              {errors.priority && (
                <span className="text-red-500 text-xs">{errors.priority.message}</span>
              )}
            </div>

          </div>

          {/* Reason */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              {...register("reason", {
                // Wrap your yup validation inside the native custom validate rule
                validate: (value) => {
                  try {
                    TrailingOrLeadingWhitespaceValidation("Reason").validateSync(value);
                    return true;
                  } catch (err: any) {
                    return err.message;
                  }
                }
              })}
              rows={3}
              placeholder="Describe why you need this asset..."
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:ring-primary focus:border-primary resize-none"
              disabled={viewMode}
            />
            {errors.reason && (
              <span className="text-red-500 text-xs">{errors.reason.message}</span>
            )}
          </div>
          {

          }

          {/* Footer */}
          <div className="mt-1 flex justify-end px-2 gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="ms-3 text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {viewMode ? "Close" : "Cancel"}
            </button>
            {!viewMode && (<button
              type="submit"
              disabled={submitting}
              className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 ms-3 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? (isEditMode ? "Updating..." : "Submitting...") : (isEditMode ? "Update Request" : "Submit Request")}
            </button>)}
          </div>
        </form>
      </FormProvider>

      {toastData && (
        <GenericToaster
          open={true}
          message={toastData.message}
          severity={toastData.severity}
          onClose={() => setToastData(null)}
        />
      )}
    </>
  );
};

export default InventoryRequestForm;